'use client';
import { useState, useEffect, useRef } from 'react';
import { Zap, Activity, Clock, Play, Terminal, Headphones, Code2, Download, Loader2, Timer, HardDrive, Copy, Link, ExternalLink } from 'lucide-react';

const formatLabel = (str: string) =>
  str ? str.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ') : 'Unknown';

type PublishStage = 'audio' | 'rendering' | 'done' | 'error';

interface PublishState {
  stage: PublishStage;
  progress: number;
  videoUrl?: string;
  fileSizeBytes?: number;
  startedAt?: number;
  finishedAt?: number;
  error?: string;
}

export default function SignalsTab({ signals }: { signals: any[] }) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [isAudioLoading, setIsAudioLoading] = useState<string | null>(null);
  const [publishStates, setPublishStates] = useState<Record<string, PublishState>>({});
  const [elapsed, setElapsed] = useState<Record<string, number>>({});
  const timerRefs = useRef<Record<string, ReturnType<typeof setInterval>>>({});

  const startTimer = (id: string) => {
    if (timerRefs.current[id]) clearInterval(timerRefs.current[id]);
    const start = Date.now();
    setElapsed(prev => ({ ...prev, [id]: 0 }));
    timerRefs.current[id] = setInterval(() => {
      setElapsed(prev => ({ ...prev, [id]: Math.floor((Date.now() - start) / 1000) }));
    }, 1000);
  };

  const stopTimer = (id: string) => {
    if (timerRefs.current[id]) {
      clearInterval(timerRefs.current[id]);
      delete timerRefs.current[id];
    }
  };

  useEffect(() => {
    return () => {
      Object.values(timerRefs.current).forEach(clearInterval);
    };
  }, []);

  const formatElapsed = (secs: number) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return m > 0 ? `${m}m ${s.toString().padStart(2, '0')}s` : `${s}s`;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const playPreview = async (id: string, text: string) => {
    setIsAudioLoading(id);
    try {
      const res = await fetch('/api/tts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, signalId: id }),
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audio.play();
    } catch (e) {
      console.error("Audio error", e);
    } finally {
      setIsAudioLoading(null);
    }
  };

  const publishToProd = async (id: string, s: any) => {
    try {
      startTimer(id);
      // Stage 1: Generate audio
      setPublishStates(prev => ({ ...prev, [id]: { stage: 'audio', progress: 0, startedAt: Date.now() } }));

      const text = s.production_data?.video_script;
      let audioUrl = s.production_data?.audio_url;

      if (!audioUrl && text) {
        const res = await fetch('/api/tts', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text, signalId: id, returnUrl: true }),
        });
        if (!res.ok) throw new Error('Audio generation failed');
        const data = await res.json();
        audioUrl = data.audioUrl;
      } else if (!audioUrl) {
        throw new Error('No script or audio available');
      }

      // Stage 2: Start Remotion Lambda render
      setPublishStates(prev => ({ ...prev, [id]: { stage: 'rendering', progress: 0 } }));

      const renderRes = await fetch('/api/render-video', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ signalId: id, audioUrl }),
      });
      if (!renderRes.ok) {
        const err = await renderRes.json();
        throw new Error(err.error || 'Render start failed');
      }
      const { renderId, bucketName } = await renderRes.json();

      // Stage 3: Poll for progress
      const poll = setInterval(async () => {
        try {
          const progressRes = await fetch(
            `/api/render-video?renderId=${renderId}&bucketName=${bucketName}&signalId=${id}`
          );
          const progress = await progressRes.json();

          if (progress.fatalErrorEncountered) {
            clearInterval(poll);
            stopTimer(id);
            setPublishStates(prev => ({
              ...prev,
              [id]: { ...prev[id], stage: 'error', progress: 0, error: 'Render failed', finishedAt: Date.now() },
            }));
            return;
          }

          if (progress.done && progress.outputFile) {
            clearInterval(poll);
            stopTimer(id);
            setPublishStates(prev => ({
              ...prev,
              [id]: { ...prev[id], stage: 'done', progress: 100, videoUrl: progress.outputFile, fileSizeBytes: progress.outputSizeInBytes, finishedAt: Date.now() },
            }));
            return;
          }

          setPublishStates(prev => ({
            ...prev,
            [id]: {
              ...prev[id],
              stage: 'rendering',
              progress: Math.round((progress.overallProgress || 0) * 100),
              fileSizeBytes: progress.outputSizeInBytes,
            },
          }));
        } catch {
          clearInterval(poll);
          stopTimer(id);
          setPublishStates(prev => ({
            ...prev,
            [id]: { ...prev[id], stage: 'error', progress: 0, error: 'Progress check failed', finishedAt: Date.now() },
          }));
        }
      }, 2000);
    } catch (e: unknown) {
      stopTimer(id);
      setPublishStates(prev => ({
        ...prev,
        [id]: {
          ...prev[id],
          stage: 'error',
          progress: 0,
          error: e instanceof Error ? e.message : 'Unknown error',
          finishedAt: Date.now(),
        },
      }));
    }
  };

  const getVideoUrl = (s: any, id: string): string | undefined => {
    return publishStates[id]?.videoUrl || s.production_data?.video_url;
  };

  return (
    <div className="animate-in fade-in duration-700">
      {/* HEADER ROW */}
      <div className="grid grid-cols-12 px-8 py-4 text-[10px] font-black uppercase tracking-[0.15em] text-slate-500 bg-white/[0.03] border-b border-white/10">
        <div className="col-span-1">Time</div>
        <div className="col-span-2">Source</div>
        <div className="col-span-3">Headline</div>
        <div className="col-span-3 text-center">Detail</div>
        <div className="col-span-1 text-center">Priority</div>
        <div className="col-span-2 text-right">Template</div>
      </div>

      <div className="divide-y divide-white/5">
        {signals.length === 0 ? (
          <div className="p-20 text-center text-slate-600 font-mono text-xs uppercase tracking-widest">
            No signals detected for this date
          </div>
        ) : signals.map((s) => {
          const ps = publishStates[s.id];
          const videoUrl = getVideoUrl(s, s.id);
          const isPublishing = ps && ps.stage !== 'done' && ps.stage !== 'error';

          return (
          <div key={s.id} className="group hover:bg-white/[0.02] transition-colors">
            {/* ROW CONTENT */}
            <div
              className="grid grid-cols-12 px-8 py-5 items-center cursor-pointer"
              onClick={() => setExpandedId(expandedId === s.id ? null : s.id)}
            >
              <div className="col-span-1 font-mono text-[10px] text-slate-500 flex items-center gap-2">
                <Clock size={10} />
                {new Date(s.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </div>
              <div className="col-span-2 flex items-center gap-2.5">
                <Activity size={14} className="text-red-600" />
                <span className="text-xs font-black uppercase text-white tracking-tight">{s.source || 'Betting Market'}</span>
              </div>
              <div className="col-span-3 text-sm font-bold text-white tracking-tight group-hover:text-red-400 transition-colors">
                {s.production_data?.visual_stack?.[0]?.props?.text || s.signal_summary}
              </div>
              <div className="col-span-3 text-[11px] text-slate-400 line-clamp-1 italic px-4 text-left">
                {s.analysis.cause}
              </div>
              <div className="col-span-1 text-center">
                <span className={`px-2 py-0.5 rounded text-[10px] font-black ${s.priority > 7 ? 'bg-red-600 text-white' : 'bg-slate-800 text-slate-400'}`}>
                  P{s.priority || 5}
                </span>
              </div>
              <div className="col-span-2 text-right text-[10px] font-black text-white uppercase tracking-widest">
                {formatLabel(s.production_data?.layout_id || s.signal_type || 'MARKET_SIGNAL')}
              </div>
            </div>

            {/* EXPANDED CONTENT - THE THIRDS LAYOUT */}
            {expandedId === s.id && (
              <div className="px-8 pb-8 pt-6 border-t border-white/5 bg-black/60 shadow-inner animate-in slide-in-from-top-4">
                <div className="grid grid-cols-3 gap-10 items-start">

                  {/* FIRST THIRD: VOICEOVER SCRIPT (Full Height) */}
                  <div className="flex flex-col h-full">
                    <div className="flex items-center gap-2 text-red-500 mb-4">
                      <Headphones size={14} />
                      <h4 className="text-[10px] font-black uppercase tracking-widest">Voiceover Script</h4>
                    </div>
                    <div className="bg-white/5 p-6 rounded-2xl border border-white/10 flex-1 relative group/script flex flex-col justify-between min-h-[250px]">
                      <p className="text-xs leading-relaxed text-slate-300 italic text-left">
                        &ldquo;{s.production_data?.video_script || "No script available."}&rdquo;
                      </p>

                      <div className="flex justify-center mt-6">
                        <button
                          disabled={isAudioLoading !== null}
                          className="bg-red-600 hover:bg-red-500 text-white px-5 py-2.5 rounded-full shadow-xl transition-all hover:scale-105 active:scale-95 flex items-center gap-2 disabled:opacity-50"
                          onClick={(e) => {
                            e.stopPropagation();
                            const txt = s.production_data?.video_script;
                            if(txt) playPreview(s.id, txt);
                          }}
                        >
                          <Play size={14} fill="white" className={isAudioLoading === s.id ? 'animate-spin' : ''} />
                          <span className="text-[11px] font-black uppercase tracking-widest">
                              {isAudioLoading === s.id ? 'Synthesizing...' : 'Preview Voice'}
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* SECOND THIRD: JSON SUBMISSION (Full Height) */}
                  <div className="flex flex-col h-full">
                    <div className="flex items-center gap-2 text-red-500 mb-4">
                      <Code2 size={14} />
                      <h4 className="text-[10px] font-black uppercase tracking-widest">JSON Submission</h4>
                      <div className="ml-auto flex items-center gap-1">
                        <button
                          title="Copy JSON text"
                          className="p-1 rounded hover:bg-white/10 text-slate-500 hover:text-white transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            const text = JSON.stringify(s.production_data || s.payload, null, 2);
                            const ta = document.createElement('textarea');
                            ta.value = text;
                            ta.style.position = 'fixed';
                            ta.style.opacity = '0';
                            document.body.appendChild(ta);
                            ta.select();
                            document.execCommand('copy');
                            document.body.removeChild(ta);
                          }}
                        >
                          <Copy size={12} />
                        </button>
                        <button
                          title="Copy JSON URL"
                          className="p-1 rounded hover:bg-white/10 text-slate-500 hover:text-white transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            const url = `https://sportsgrid-labs.sportsgrid.com/api/ingest?id=${s.id}`;
                            const ta = document.createElement('textarea');
                            ta.value = url;
                            ta.style.position = 'fixed';
                            ta.style.opacity = '0';
                            document.body.appendChild(ta);
                            ta.select();
                            document.execCommand('copy');
                            document.body.removeChild(ta);
                          }}
                        >
                          <Link size={12} />
                        </button>
                        <button
                          title="Open JSON in new tab"
                          className="p-1 rounded hover:bg-white/10 text-slate-500 hover:text-white transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(`https://sportsgrid-labs.sportsgrid.com/api/ingest?id=${s.id}`, '_blank');
                          }}
                        >
                          <ExternalLink size={12} />
                        </button>
                      </div>
                    </div>
                    <pre className="p-4 bg-slate-950 rounded-xl border border-white/10 text-[11px] font-mono text-red-400/60 flex-1 overflow-y-auto scrollbar-hide max-h-[300px]">
                      {JSON.stringify(s.production_data || s.payload, null, 2)}
                    </pre>
                  </div>

                  {/* THIRD THIRD: VISUAL LOGIC + CONVICTION */}
                  <div className="flex flex-col gap-6">
                    <div>
                      <div className="flex items-center gap-2 text-red-500 mb-4">
                        <Terminal size={14} />
                        <h4 className="text-[10px] font-black uppercase tracking-widest">Visual Logic</h4>
                      </div>
                      <div className="space-y-2">
                        {s.production_data?.visual_stack?.map((mod: any, i: number) => (
                          <div key={i} className="flex justify-between items-center text-[11px] bg-white/5 p-3 rounded-lg border border-white/5">
                            <span className="text-slate-300 font-bold">{formatLabel(mod.module_id)}</span>
                            <span className="text-red-500 font-mono font-bold">{mod.start_s}s → {mod.duration_s}s</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* MARKET CONVICTION (STANDALONE LINE) */}
                    <div className="pt-4 border-t border-white/5 flex items-center gap-2">
                      <span className="text-[10px] font-black text-red-500 uppercase tracking-widest">Market Conviction:</span>
                      <span className="text-xl font-black text-white font-mono">
                        {s.production_data?.sources?.[0]?.current_price ? Math.round(s.production_data.sources[0].current_price * 100) : 85}%
                      </span>
                    </div>

                    {/* PUBLISH ACTION + PROGRESS */}
                    <div className="mt-auto pt-6 flex flex-col gap-3">
                      <button
                        disabled={isPublishing}
                        onClick={(e) => {
                          e.stopPropagation();
                          publishToProd(s.id, s);
                        }}
                        className="w-full bg-white text-black hover:bg-red-600 hover:text-white py-3 rounded-lg text-[11px] font-black uppercase tracking-widest transition-all shadow-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isPublishing ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : (
                          <Zap size={14} fill="currentColor" />
                        )}
                        {isPublishing ? 'Processing...' : 'Publish to Production'}
                      </button>

                      {/* PROGRESS BAR */}
                      {ps && ps.stage !== 'done' && (
                        <div className="space-y-2">
                          <div className="w-full bg-slate-800 rounded-full h-2 overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all duration-500 ${
                                ps.stage === 'error' ? 'bg-red-500' : 'bg-red-600'
                              }`}
                              style={{
                                width: ps.stage === 'audio'
                                  ? '15%'
                                  : ps.stage === 'error'
                                    ? '100%'
                                    : `${Math.max(20, ps.progress)}%`,
                              }}
                            />
                          </div>
                          <p className="text-[10px] font-mono text-center tracking-wide">
                            {ps.stage === 'audio' && (
                              <span className="text-amber-400">Generating audio...</span>
                            )}
                            {ps.stage === 'rendering' && (
                              <span className="text-sky-400">
                                Rendering video... {ps.progress}%
                              </span>
                            )}
                            {ps.stage === 'error' && (
                              <span className="text-red-400">
                                Error: {ps.error}
                              </span>
                            )}
                          </p>
                          {/* Elapsed time + file size during render */}
                          {ps.stage !== 'error' && (
                            <div className="flex items-center justify-center gap-4 text-[10px] font-mono text-slate-500">
                              <span className="flex items-center gap-1">
                                <Timer size={10} />
                                {formatElapsed(elapsed[s.id] ?? 0)}
                              </span>
                              {ps.fileSizeBytes != null && ps.fileSizeBytes > 0 && (
                                <span className="flex items-center gap-1">
                                  <HardDrive size={10} />
                                  {formatFileSize(ps.fileSizeBytes)}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {/* DOWNLOAD VIDEO BUTTON + STATS */}
                      {videoUrl && (
                        <div className="space-y-2">
                          <a
                            href={videoUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="w-full bg-emerald-600 hover:bg-emerald-500 text-white py-3 rounded-lg text-[11px] font-black uppercase tracking-widest transition-all shadow-lg flex items-center justify-center gap-2"
                            download
                          >
                            <Download size={14} />
                            Download Video
                          </a>
                          {ps?.stage === 'done' && (
                            <div className="flex items-center justify-center gap-4 text-[10px] font-mono text-slate-500">
                              {ps.startedAt && ps.finishedAt && (
                                <span className="flex items-center gap-1">
                                  <Timer size={10} className="text-emerald-500" />
                                  <span className="text-emerald-500">{formatElapsed(Math.floor((ps.finishedAt - ps.startedAt) / 1000))}</span>
                                </span>
                              )}
                              {ps.fileSizeBytes != null && ps.fileSizeBytes > 0 && (
                                <span className="flex items-center gap-1">
                                  <HardDrive size={10} className="text-emerald-500" />
                                  <span className="text-emerald-500">{formatFileSize(ps.fileSizeBytes)}</span>
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      <p className="text-[9px] text-slate-600 text-center font-mono italic">
                        Target: AWS-S3-PROD-EAST-1
                      </p>
                    </div>
                  </div>

                </div>
              </div>
            )}
           </div>
          );
        })}
      </div>
    </div>
  );
}
